import { RestService } from './RestService';
import { v4 as uuidv4 } from 'uuid';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extendFormData, getNextPageParam, MULTIPART_HEADERS, QUERY_KEYS } from '../config/Config.ts';

export enum CONTENT_TYPES {
  IMAGES = 'IMAGES',
  STORIES = 'STORIES',
  PUZZLES = 'PUZZLES'
}

export const fetchDrawings = ({ pageParam = 1 }) => {
  return RestService.get(`/drawings?page=${pageParam}&source=web`);
};

export const useDrawingsData = () => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.DRAWINGS],
    queryFn: fetchDrawings,
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, pages: any) => getNextPageParam(lastPage, pages)
  });
};

const handleAddDrawingMutation = async (queryKey: any, queryClient: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any, index: number) => {
          return {
            ...pageData,
            data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
          };
        })
      };
    });
  }
};

export const useAddDrawingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: any) => {
      const finalData = { uuid: uuidv4() };
      extendFormData(formData, finalData);
      return RestService.post('/drawings', formData, MULTIPART_HEADERS);
    },
    onSuccess: async (newData: any) => {
      await handleAddDrawingMutation([QUERY_KEYS.DRAWINGS], queryClient, newData);
    }
  });
};

const handleUpdateDrawingMutation = async (queryKey: any, queryClient: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any) => {
        return {
          ...pageData,
          data: pageData.data.map((item: any) => {
            if (item.uuid === newData.data.uuid) {
              return newData.data;
            }
            return item;
          })
        };
      })
    };
  });
};

export const useUpdateDrawingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, formData }: any) => {
      return RestService.update('/drawings/' + uuid, formData, MULTIPART_HEADERS);
    },
    onSuccess: async (newData: any) => {
      await handleUpdateDrawingMutation([QUERY_KEYS.DRAWINGS], queryClient, newData);
    }
  });
};

const handleDeleteDrawingMutation = async (queryKey: any, queryClient: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      pages: oldQueryData.pages.map((pageData: any) => {
        return {
          ...pageData,
          data: pageData.data.filter((item: any) => item.uuid !== newData.data.uuid)
        };
      })
    };
  });
};

export const useDeleteDrawingMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/drawings/' + uuid),
    onSuccess: async (newData: any) => {
      await handleDeleteDrawingMutation([QUERY_KEYS.DRAWINGS], queryClient, newData);
    }
  });
};
